import React from 'react'
import {
  makeStyles,
  styled,
  Radio,
  CircularProgress,
  Backdrop
} from '@material-ui/core'

import moment from 'moment'
import heic2any from 'heic2any'
import { Field } from 'redux-form'
import HighlightOffIcon from '@material-ui/icons/HighlightOff'
import { isEmpty } from 'lodash'
import InputSelect from '../select/InputSelect'

import Button from '../button/Button'
import MaterialInput from '../shared/MaterialInput'
import { TimePickerInput } from '../shared/DatePickerInput'
import RelationSelect from '../select/RelationshipSelect'
import {
  required,
  validEmail,
  capitalize,
  normalizePhone,
  normalizeCPF,
  validPhone,
  isValidCPF
} from '../../utils/formUtils'
import { handleAddAvatar } from '../../utils/helpers'
import StyledSVG from '../shared/StyledSVG'
import crossAddSVG from '../../assets/icons/cross-add-icon.svg'

const useStyles = makeStyles(theme => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1
  },
  modal_root: {
    fontFamily: 'Montserrat, Source Sans Pro',
    backgroundColor: '#FFFFFF',
    borderRadius: 24,
    padding: '56px 64px',
    width: 700,
    height: 980
  },
  headers: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 36,
    '& h1': {
      color: '#386093',
      fontSize: 24,
      fontWeight: 600,
      margin: 0
    },
    '& p': {
      color: '#9B9B9B',
      fontSize: 16,
      fontWeight: 400,
      margin: 0
    }
  },
  bottom_btns: {
    display: 'flex',
    gap: 12,
    justifyContent: 'flex-end',
    paddingBottom: 16,
    marginTop: 36
  },
  cancel_btn: {
    width: 146,
    border: '1px solid #EF7C8F',
    height: 44,
    padding: '14px 48px',
    gap: 10,
    color: '#EF7C8F',
    borderRadius: 8,
    '&:hover': {
      border: '1px solid #EF7C8F'
    },
    fontSize: '16px',
    fontWeight: 600,
    textTransform: 'capitalize',
    lineHeight: '19.5px'
  },
  send_btn: {
    backgroundColor: '#386093',
    width: 148,
    height: 44,
    padding: '14px 48px',
    gap: 10,
    borderRadius: 8,
    '&:hover': {
      backgroundColor: '#386093'
    },
    fontSize: '16px',
    fontWeight: 600,
    textTransform: 'capitalize',
    lineHeight: '19.5px'
  },
  time_picker_class: {
    width: 100
  },
  remove_photo: {
    position: 'absolute',
    right: 2,
    top: 2
  },
  search_component: {
    width: '100%',
    height: 40,
    padding: '8px 12px',
    borderRadius: '8px',
    border: '1px solid #D2D5D8',
    '& input': {
      padding: 0
    }
  },
  identification_infos_wrapper: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 24
  },
  identification_item: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    '& .label': {
      fontSize: 12,
      fontWeight: 600,
      margin: 0
    }
  },
  options: {
    '& .child_name_wrapper': {
      height: 48,
      display: 'flex',
      alignItems: 'center',
      '&:hover': {
        backgroundColor: '#F5F6F7'
      },
      '& p': {
        paddingLeft: 8,
        margin: 0,
        cursor: 'pointer'
      }
    }
  },
  children_seleted: {
    backgroundColor: '#E9FAD9 !important',
    '&:hover': {
      backgroundColor: '#E9FAD9 !important'
    }
  },
  pagination: {
    float: 'right',
    paddingTop: '15px',
    [theme.breakpoints.down('426')]: {
      float: 'unset',
      '& .MuiPagination-ul': {
        flexWrap: 'nowrap'
      }
    }
  },
  child_pagination_wrapper: {
    width: '100%',
    '& .MuiPagination-root': {
      padding: '0px 24px 12px 0px'
    }
  },
  selected_childrens: {
    display: 'flex',
    columnGap: 8,
    rowGap: 12,
    flexWrap: 'wrap',
    marginTop: 8
  },
  selected_childrens_item: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: 8,
    borderRadius: 8,
    backgroundColor: '#D9E8FA',
    '& p': {
      margin: 0,
      fontSize: 14,
      fontWeight: 500
    },
    '& .remove_child': {
      cursor: 'pointer',
      '& path': {
        fill: '#222A33'
      }
    }
  },
  radio_wrapper: {
    display: 'flex',
    gap: 24,
    '& .radioButton': {
      display: 'flex'
    }
  },
  authorization_type: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginTop: 16,
    '& .label': {
      margin: 0,
      fontWeight: 600,
      fontSize: 16
    }
  },
  contact_infos: {
    marginTop: 24,
    display: 'grid',
    gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
    columnGap: 24,
    rowGap: 32
  },
  material_input_class: {
    width: '100%'
  },
  search_field: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 8,
    height: 'fit-content',
    '& p.label': {
      margin: 0,
      fontSize: 12,
      fontWeight: 600
    }
  }
}))

const FormFieldWrapper = styled('div')(({ theme, gridColumn }) => ({
  gridColumn: `span ${gridColumn || 6}`
}))

const PhotoInput = styled('div')(({ theme, bgImg }) => ({
  width: 56,
  height: 56,
  borderRadius: 8,
  position: 'relative',
  backgroundSize: 'cover',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px black dashed',
  backgroundImage: bgImg
}))

const CreatePickupAuthorizationsForm = ({
  getPickupAuthorizations,
  handleCloseModal,
  createPickupAuthorizationRSPA,
  editPickupAuthorizationRSPA,
  alert,
  valid,
  currentUser,
  schoolId,
  getChildrenRSPA,
  fieldValues,
  selectedAuthorization,
  getAuthorizationById,
  initialValues,
  setSelectedAuthorization,
  clearCurrentAuthorization
}) => {
  const classes = useStyles()
  const reponsiblePhotoRef = React.useRef(null)
  const [selectedChildrens, setSelectedChildrens] = React.useState([])
  const [openBackdrop, setOpenBackdrop] = React.useState(false)
  const [permanentAuthorization, setPermanentAuthorization] = React.useState({
    value: true,
    days: []
  })
  const [uploadingInProgress, setUploadingInProgress] = React.useState(false)
  const [responsiblePhoto, setResponsiblePhoto] = React.useState({
    url: null,
    file: null
  })
  const loadedAuthorization = !isEmpty(initialValues)

  // pegar os dados ja cadastrados e limpar ao fechar
  React.useEffect(
    () => {
      if (selectedAuthorization) {
        getAuthorizationById({ id: selectedAuthorization })
      }
      return () => {
        setSelectedAuthorization(null)
        clearCurrentAuthorization()
      }
    },
    [selectedAuthorization]
  )

  // setar dados que nao estão atrelados ao redux-form
  React.useEffect(
    () => {
      if (loadedAuthorization) {
        setSelectedChildrens(
          initialValues?.pickup_authorization_children.map(item => ({
            name: item?.child?.name,
            id: +item?.child?.id
          }))
        )

        const permanentAuthorization = !!(
          String(initialValues?.pickup_type) === 'permanent'
        )
        setPermanentAuthorization(prev => ({
          ...prev,
          value: permanentAuthorization
        }))

        setResponsiblePhoto(prev => {
          return { ...prev, url: initialValues?.photo_url }
        })
      }
    },
    [initialValues]
  )

  React.useEffect(
    () => {
      return () => {
        if (responsiblePhoto.url) {
          URL.revokeObjectURL(responsiblePhoto.url)
        }
      }
    },
    [responsiblePhoto]
  )

  const handleChangePhoto = event => {
    handleAddAvatar(
      event,
      setUploadingInProgress,
      alert,
      param =>
        setResponsiblePhoto(() => {
          return { file: param, url: URL.createObjectURL(param) }
        }),
      heic2any
    )
  }

  const handleRemovePhoto = event => {
    if (event && event.stopPropagation) event.stopPropagation()
    setResponsiblePhoto(prev => {
      URL.revokeObjectURL(prev.url)
      return { url: null, file: null }
    })
  }

  const handleClickPhotoInput = () => {
    reponsiblePhotoRef.current && reponsiblePhotoRef.current.click()
  }

  const handleSubmitForm = () => {
    if (valid && selectedChildrens.length > 0) {
      const { pickup_estimage, ...rest } = fieldValues
      const basicData = {
        authorized_by_id: currentUser.id,
        pickup_type: permanentAuthorization.value ? 'permanent' : 'temporary',
        ...(!permanentAuthorization.value
          ? { estimated_arrival: moment(pickup_estimage).format('HH:mm') }
          : {}),
        photo: responsiblePhoto.file,
        pickup_authorization_children_attributes: selectedChildrens.map(
          item => ({
            child_id: item.id
          })
        )
      }
      setOpenBackdrop(true)
      if (selectedAuthorization) {
        const {
          name,
          email,
          phone,
          relationship_type,
          taxpayer_number,
          vehicle_plate
        } = fieldValues
        return editPickupAuthorizationRSPA({
          id: selectedAuthorization,
          data: {
            ...basicData,
            name,
            email,
            phone,
            relationship_type,
            taxpayer_number,
            vehicle_plate
          }
        })
          .then(res => {
            alert('Autorização editada com sucesso!', 'success')
          })
          .catch(err => {
            console.error(err)
            alert('Erro ao editar autorização.', 'error')
          })
          .finally(() => {
            handleCloseModal()
            getPickupAuthorizations({ page: 1 })
            setOpenBackdrop(false)
          })
      }
      createPickupAuthorizationRSPA({
        ...rest,
        ...basicData,
        school_id: schoolId
      })
        .then(res => {
          alert('Autorização criada com sucesso!', 'success')
        })
        .catch(err => {
          console.error(err)
          alert('Erro ao criar autorização.', 'error')
        })
        .finally(() => {
          handleCloseModal()
          getPickupAuthorizations({ page: 1 })
          setOpenBackdrop(false)
        })
    }
  }

  const handleChangeAuthorizationType = canPick => {
    setPermanentAuthorization(prev => ({ ...prev, value: canPick }))
  }

  const handleUnselectForm = () => {
    handleCloseModal()
  }
  if (selectedAuthorization && isEmpty(initialValues)) {
    return (
      <div className={classes.modal_root}>
        <CircularProgress />
      </div>
    )
  }
  return (
    <div className={classes.modal_root}>
      <div className={classes.headers}>
        <h1>Criar nova autorização</h1>
        <p>Adicione quem poderá retirar o aluno.</p>
      </div>
      <InputSelect
        isMulti
        selectedItems={selectedChildrens}
        setSelectedItems={setSelectedChildrens}
        wrapperClass={classes.input_wrapper}
        fetchItems={getChildrenRSPA}
        placeholder={'Digite o(s) nome(s) do(s) aluno(s)'}
        label={'Aluno'}
        params={{
          'page[size]': 7
        }}
      />
      {!loadedAuthorization && (
        <div className={classes.authorization_type}>
          <p className='label'>Tipo de autorização:</p>
          <div className={classes.radio_wrapper}>
            <div className='radioButton'>
              <Radio
                value={!!permanentAuthorization.value}
                checked={!!permanentAuthorization.value}
                onChange={() => handleChangeAuthorizationType(true)}
              />
              <p>Permanente</p>
            </div>
            <div className='radioButton'>
              <Radio
                value={!permanentAuthorization.value}
                checked={!permanentAuthorization.value}
                onChange={() => handleChangeAuthorizationType(false)}
              />
              <p>Temporária</p>
            </div>
          </div>
        </div>
      )}
      <div className={classes.contact_infos}>
        <FormFieldWrapper gridColumn={8} className={`${classes.search_field}`}>
          <p className='label'>Nome completo do responsável</p>
          <Field
            rootClass={classes.material_input_class}
            component={MaterialInput}
            name='name'
            placeholder='Digite o nome completo'
            type='text'
            autoComplete='off'
            normalize={capitalize}
            validate={[required]}
          />
        </FormFieldWrapper>
        <FormFieldWrapper gridColumn={4} className={`${classes.search_field}`}>
          <p className='label'>Relação com a criança</p>
          <Field
            rootClass={classes.material_input_class}
            component={RelationSelect}
            name='relationship_type'
            placeholder='Ex: Tio'
          />
        </FormFieldWrapper>
        <FormFieldWrapper className={`${classes.search_field}`}>
          <p className='label'>CPF</p>
          <Field
            allowClear
            rootClass={classes.material_input_class}
            component={MaterialInput}
            name={`taxpayer_number`}
            placeholder='CPF'
            type='text'
            autoComplete='off'
            normalize={normalizeCPF}
            validate={[required, isValidCPF]}
          />
        </FormFieldWrapper>
        <FormFieldWrapper className={`${classes.search_field}`}>
          <p className='label'>Telefone de contato</p>
          <Field
            rootClass={classes.material_input_class}
            component={MaterialInput}
            name='phone'
            placeholder='Ex:(61) 99999-9999'
            type='text'
            autoComplete='off'
            normalize={normalizePhone}
            validate={[required, validPhone]}
          />
        </FormFieldWrapper>
        <FormFieldWrapper className={`${classes.search_field}`}>
          <p className='label'>Email do responsável</p>
          <Field
            rootClass={classes.material_input_class}
            component={MaterialInput}
            name='email'
            placeholder='Digite o email'
            type='email'
            autoComplete='off'
            validate={[required, validEmail]}
          />
        </FormFieldWrapper>
        <FormFieldWrapper className={`${classes.search_field}`}>
          <p className='label'>Placa do veículo</p>
          <Field
            rootClass={classes.material_input_class}
            component={MaterialInput}
            name='vehicle_plate'
            placeholder='Ex:ABC1D23'
            type='vehicle_plate'
            autoComplete='off'
          />
        </FormFieldWrapper>
      </div>
      <div className={classes.identification_infos_wrapper}>
        <div className={classes.identification_item}>
          <p className='label'>Foto do responsável</p>
          <PhotoInput
            bgImg={`url(${responsiblePhoto.url})`}
            onClick={handleClickPhotoInput}
          >
            {!uploadingInProgress ? (
              !responsiblePhoto.url && (
                <StyledSVG src={crossAddSVG} width={18} height={18} />
              )
            ) : (
              <CircularProgress />
            )}
            {responsiblePhoto.url && (
              <HighlightOffIcon
                onClick={handleRemovePhoto}
                className={classes.remove_photo}
              />
            )}
            <input
              accept='.png, .jpg, .jpeg, .heic'
              hidden
              name='avatar'
              onChange={handleChangePhoto}
              ref={reponsiblePhotoRef}
              type='file'
            />
          </PhotoInput>
        </div>
        {!permanentAuthorization.value && (
          <div className={classes.identification_item}>
            <p className='label'>Estimativa de chegada</p>
            <Field
              rootClass={classes.time_picker_class}
              component={TimePickerInput}
              defaultValue={initialValues?.estimated_arrival}
              id='pickup_estimate'
              name='pickup_estimate'
              placeholder='00:00'
            />
          </div>
        )}
      </div>
      <div className={classes.bottom_btns}>
        <Button
          onclick={() => handleUnselectForm()}
          type='button'
          variant='outlined'
          className={classes.cancel_btn}
        >
          Cancelar
        </Button>
        <Button
          onClick={() => handleSubmitForm()}
          disabled={!valid || selectedChildrens.length === 0}
          className={classes.send_btn}
        >
          Criar
        </Button>
      </div>
      <Backdrop className={classes.backdrop} open={openBackdrop}>
        <CircularProgress color='secondary' />
      </Backdrop>
    </div>
  )
}

export default CreatePickupAuthorizationsForm
